// src/components/student/AllStudents.jsx
import React, { useState, useEffect } from 'react';
import { Users } from 'lucide-react';
import { useStudentContext } from '../../contexts/StudentContext';
import { updateDriverAssignment } from '../../services/studentService';
import { getAllDrivers } from '../../services/driverService';
import { styles } from '../../styles/theme';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';


// Edit After School Student Modal Component
const EditAfterSchoolStudentModal = ({ student, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    pickupDays: (() => {
      if (Array.isArray(student.pickupDays)) {
        return student.pickupDays;
      }
      if (typeof student.pickupDays === 'string') {
        return student.pickupDays.split(/[,\n]+/).map(d => d.trim()).filter(d => d);
      }
      return [];
    })(),
    dismissalTimeNormal: student.dismissalTimeNormal || '15:30',
    dismissalTimeWednesday: student.dismissalTimeWednesday || '14:15',
    programDaysPerWeek: student.programDaysPerWeek || '4 days'
  });

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

  const toggleDay = (day) => {
    setFormData(prev => ({
      ...prev,
      pickupDays: prev.pickupDays.includes(day)
        ? prev.pickupDays.filter(d => d !== day)
        : [...prev.pickupDays, day]
    }));
  };

  const handleSave = async () => {
    try {
      const updateData = {
        ...formData,
        pickupDays: formData.pickupDays.join(', '),
        programDaysPerWeek: `${formData.pickupDays.length} days`
      };

      await onSave(student.id, updateData);
      onClose();
    } catch (error) {
      console.error('Error saving:', error);
      alert('Failed to save changes');
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '2rem',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <h2 style={{ marginBottom: '1.5rem', color: '#1f2937' }}>
          Edit After School Schedule
        </h2>
        
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>
            Student: {student.firstName} {student.lastName}
          </h3>
          <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>
            Program: {student.programSelection}
          </p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ 
            display: 'block', 
            fontSize: '0.875rem', 
            fontWeight: '500', 
            marginBottom: '0.5rem',
            color: '#374151'
          }}>
            Pickup Days *
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {daysOfWeek.map(day => (
              <label key={day} style={{
                display: 'flex',
                alignItems: 'center',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                cursor: 'pointer',
                backgroundColor: formData.pickupDays.includes(day) ? '#e0f2fe' : 'white'
              }}>
                <input
                  type="checkbox"
                  checked={formData.pickupDays.includes(day)}
                  onChange={() => toggleDay(day)}
                  style={{ marginRight: '0.75rem' }}
                />
                <span style={{ 
                  color: formData.pickupDays.includes(day) ? '#0c4a6e' : '#374151',
                  fontWeight: formData.pickupDays.includes(day) ? '600' : '400'
                }}>
                  {day}
                </span>
              </label>
            ))}
          </div>
          <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
            Selected: {formData.pickupDays.length} day(s) per week
          </p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ 
            display: 'block', 
            fontSize: '0.875rem', 
            fontWeight: '500', 
            marginBottom: '0.5rem',
            color: '#374151'
          }}>
            Dismissal Time (Mon/Tue/Thu/Fri)
          </label>
          <input
            type="time"
            value={formData.dismissalTimeNormal}
            onChange={(e) => setFormData(prev => ({ ...prev, dismissalTimeNormal: e.target.value }))}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '4px'
            }}
          />
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ 
            display: 'block', 
            fontSize: '0.875rem', 
            fontWeight: '500', 
            marginBottom: '0.5rem',
            color: '#374151'
          }}>
            Dismissal Time (Wednesday)
          </label>
          <input
            type="time"
            value={formData.dismissalTimeWednesday}
            onChange={(e) => setFormData(prev => ({ ...prev, dismissalTimeWednesday: e.target.value }))}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '4px'
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
          <button
            onClick={handleSave}
            disabled={formData.pickupDays.length === 0}
            style={{
              flex: 1,
              padding: '0.75rem',
              backgroundColor: formData.pickupDays.length === 0 ? '#d1d5db' : '#00A3E0',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontWeight: '600',
              cursor: formData.pickupDays.length === 0 ? 'not-allowed' : 'pointer'
            }}
          >
            Save Changes
          </button>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '0.75rem',
              backgroundColor: 'white',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const STOP_REASONS = ['Discontinued Program', 'Moved away', 'Cost', 'Schedule conflict', 'School change', 'Program not a fit', 'Graduated/Too old', 'Other'];

const AllStudents = ({ onBack, onEdit }) => {
  const { getActiveStudents } = useStudentContext();
  const [updatingDriver, setUpdatingDriver] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(true);
  const [editingStudent, setEditingStudent] = useState(null);
  const [inactiveModal, setInactiveModal] = useState(null);
  const [inactiveForm, setInactiveForm] = useState({ reason: 'Discontinued Program', dateStopped: new Date().toISOString().split('T')[0], effectiveDate: new Date().toISOString().split('T')[0], notes: '' });
  const [savingInactive, setSavingInactive] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    loadDrivers();
  }, []);

  const loadDrivers = async () => {
    try {
      setLoadingDrivers(true);
      const driversData = await getAllDrivers();
      setDrivers(driversData);
    } catch (error) {
      console.error('Error loading drivers:', error);
      setDrivers([]);
    } finally {
      setLoadingDrivers(false);
    }
  };

  const handleSaveAfterSchoolStudent = async (studentId, updateData) => {
    try {
      const studentRef = doc(db, 'students', studentId);
      await updateDoc(studentRef, {
        ...updateData,
        lastUpdated: serverTimestamp()
      });
      
      console.log('✅ After school student updated:', studentId);
      alert('Schedule updated successfully!');
    } catch (error) {
      console.error('❌ Error updating student:', error);
      alert(`Failed to update: ${error.message}`);
      throw error;
    }
  };

  const handleMarkInactive = async () => {
    if (!inactiveModal) return;
    setSavingInactive(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const effectiveDate = inactiveForm.effectiveDate || today;
      const isFuture = effectiveDate > today;

      await updateDoc(doc(db, 'students', inactiveModal.id), {
        // Only set inactive immediately if effective date is today or past
        programStatus: isFuture ? 'active' : 'inactive',
        onBreak: false,
        inactiveReason: inactiveForm.reason,
        inactiveDate: effectiveDate,
        inactiveNotes: inactiveForm.notes,
        lastUpdated: serverTimestamp(),
      });

      setInactiveModal(null);
      setInactiveForm({ reason: 'Discontinued Program', dateStopped: today, effectiveDate: today, notes: '' });

      const msg = isFuture
        ? `✅ ${inactiveModal.firstName} ${inactiveModal.lastName} will be discontinued on ${new Date(effectiveDate + 'T12:00:00').toLocaleDateString()}. They remain active until then.`
        : `✅ ${inactiveModal.firstName} ${inactiveModal.lastName} discontinued — removed from roster.`;
      alert(msg);
    } catch (err) {
      alert('Failed to discontinue student.');
    } finally {
      setSavingInactive(false);
    }
  };

  const handleReactivateStudent = async (student) => {
    if (!window.confirm(`Re-activate ${student.firstName} ${student.lastName}?`)) return;
    try {
      await updateDoc(doc(db, 'students', student.id), {
        programStatus: 'active',
        inactiveReason: null,
        inactiveDate: null,
        inactiveNotes: null,
        lastUpdated: serverTimestamp(),
      });
    } catch (err) {
      alert('Failed to re-activate student.');
    }
  };

  const activeStudents = getActiveStudents().filter(s => s.programStatus !== 'inactive');
  const inactiveStudents = getActiveStudents().filter(s => s.programStatus === 'inactive');
  const pickupStudents = activeStudents.filter(s => s.needsPickup);
  const nonPickupStudents = activeStudents.filter(s => !s.needsPickup);
  const uniqueSchools = new Set(activeStudents.filter(s => s.schoolName).map(s => s.schoolName));

  const handleDriverChange = async (studentId, newDriverId) => {
    setUpdatingDriver(studentId);
    try {
      await updateDriverAssignment(studentId, newDriverId);
    } catch (error) {
      alert('Failed to update driver assignment');
    } finally {
      setUpdatingDriver(null);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <div style={styles.header}>
        <div style={styles.centerContainer}>
          <button style={styles.backButton} onClick={onBack}>
            ← Back to Portal
          </button>
          <h1 style={styles.headerTitle}>All Registered Students</h1>
          <p style={styles.headerSubtitle}>Complete student registry - {activeStudents.length} students total</p>
        </div>
      </div>

      <div style={styles.centerContainer}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          <div style={{...styles.statsCard, borderLeft: '4px solid #3b82f6'}}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3b82f6' }}>
              {activeStudents.length}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total Students</div>
          </div>
          <div style={{...styles.statsCard, borderLeft: '4px solid #10b981'}}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981' }}>
              {pickupStudents.length}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>With Pickup</div>
          </div>
          <div style={{...styles.statsCard, borderLeft: '4px solid #6b7280'}}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#6b7280' }}>
              {nonPickupStudents.length}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>No Pickup</div>
          </div>
          <div style={{ ...styles.statsCard, borderLeft: '4px solid #f59e0b' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f59e0b' }}>
              {uniqueSchools.size}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Schools</div>
          </div>
          <div
            onClick={() => setShowInactive(v => !v)}
            style={{ ...styles.statsCard, borderLeft: '4px solid #6b7280', cursor: 'pointer' }}
          >
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#6b7280' }}>
              {inactiveStudents.length}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Inactive {showInactive ? '▲' : '▼'}</div>
          </div>
        </div>

        <div style={styles.card}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Student</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Grade</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>School</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Parent</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Contact</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Program</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Pickup</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Driver</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Actions</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Registration</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const campStudents = activeStudents.filter(s => 
                    s.billingType === 'bright-horizons' || s.billingType === 'third-party'
                  );
                  
                  const regularStudents = activeStudents.filter(s => 
                    s.billingType !== 'bright-horizons' && s.billingType !== 'third-party'
                  );
                  
                  const groupedCampStudents = {};
                  campStudents.forEach(student => {
                    const key = `${student.firstName}-${student.lastName}-${student.parentEmail}`.toLowerCase();
                    if (!groupedCampStudents[key]) {
                      groupedCampStudents[key] = {
                        ...student,
                        allCareDates: [],
                        careRequestNumbers: []
                      };
                    }
                    if (student.careDates && Array.isArray(student.careDates)) {
                      groupedCampStudents[key].allCareDates.push(...student.careDates);
                    }
                    if (student.careRequestNumber) {
                      groupedCampStudents[key].careRequestNumbers.push(student.careRequestNumber);
                    }
                  });
                  
                  const displayStudents = [
                    ...Object.values(groupedCampStudents),
                    ...regularStudents
                  ];
                  
                  return displayStudents.map((student, index) => {
                    const isCampStudent = student.allCareDates && student.allCareDates.length > 0;
                    const isAfterSchoolStudent = student.programSelection?.includes('After School');
                    
                    return (
                      <tr key={student.id} style={{ 
                        borderBottom: '1px solid #f3f4f6', 
                        backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb' 
                      }}>
                        <td style={{ padding: '1rem' }}>
                          <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                            {student.firstName} {student.lastName}
                          </div>
                          {isCampStudent && (
                            <div style={{ fontSize: '0.75rem', color: '#8b5cf6', marginTop: '0.25rem' }}>
                              📅 {student.allCareDates.length} camp day(s) reserved
                            </div>
                          )}
                          {student.inactiveDate && student.programStatus !== 'inactive' && student.inactiveDate > new Date().toISOString().split('T')[0] && (
                            <div style={{ fontSize: '0.7rem', color: '#d97706', marginTop: '0.25rem', backgroundColor: '#fffbeb', padding: '0.15rem 0.4rem', borderRadius: '3px', display: 'inline-block', fontWeight: '600' }}>
                              ⏳ Discontinues {new Date(student.inactiveDate + 'T12:00:00').toLocaleDateString()}
                            </div>
                          )}
                          {student.medicalConditions && student.medicalConditions !== 'None' && (
                            <div style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.25rem' }}>
                              ⚠️ {student.medicalConditions}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                          {student.grade || '-'}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                          {isCampStudent ? (
                            <div>
                              <div style={{ fontWeight: '500', color: '#8b5cf6' }}>Camp Student</div>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                              {student.allCareDates
  .filter(cd => cd && cd.date)
  .sort((a, b) => {
    const parseCareDateStr = (s) => {
      const [m, d, y] = s.split('-').map(Number);
      return new Date(y < 100 ? 2000 + y : y, m - 1, d);
    };
    return parseCareDateStr(a.date) - parseCareDateStr(b.date);
  })
  .slice(0, 3)
  .map(cd => {
    // cd.date is MM-DD-YY — expand year to MM-DD-YYYY
    const parts = cd.date.split('-');
    if (parts.length === 3 && parts[2].length === 2) {
      return `${parts[0]}-${parts[1]}-20${parts[2]}`;
    }
    return cd.date;
  })
  .join(', ')}
                                {student.allCareDates.length > 3 && ` +${student.allCareDates.length - 3} more`}
                              </div>
                            </div>
                          ) : (
                            student.schoolName || '-'
                          )}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                          {student.parentName || '-'}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                          <div style={{ color: '#6b7280' }}>{student.parentPhone || '-'}</div>
                          <div style={{ color: '#9ca3af', fontSize: '0.75rem' }}>{student.parentEmail || '-'}</div>
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                          {isCampStudent ? 'Full-Day Camp (BH)' : (student.programSelection || '-')}
                        </td>
                        <td style={{ padding: '1rem', textAlign: 'center' }}>
                          {student.needsPickup ? (
                            <span style={{ ...styles.badge, backgroundColor: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7' }}>
                              Yes
                            </span>
                          ) : (
                            <span style={{ ...styles.badge, backgroundColor: '#f3f4f6', color: '#6b7280', border: '1px solid #d1d5db' }}>
                              No
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '1rem', textAlign: 'center', fontSize: '0.875rem' }}>
                         
{student.needsPickup ? (
  <select
    value={student.assignedDriver || ''}
    onChange={(e) => handleDriverChange(student.id, e.target.value)}
    disabled={updatingDriver === student.id || loadingDrivers}
    style={{
      ...styles.select,
      marginBottom: 0,
      padding: '0.5rem',
      fontSize: '0.75rem',
      cursor: updatingDriver === student.id ? 'wait' : 'pointer',
      opacity: updatingDriver === student.id ? 0.6 : 1
    }}
  >
    <option value="">-- Select Driver --</option>
    {drivers.map(driver => (
      <option key={driver.id} value={driver.id}>
        {driver.name} {driver.status !== 'active' ? `(${driver.status})` : ''}
      </option>
    ))}
  </select>
) : (
  <span style={{ color: '#9ca3af' }}>-</span>
)}
                        </td>
                        <td style={{ padding: '1rem', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button
                              style={{ ...styles.button, padding: '0.5rem 1rem', fontSize: '0.75rem', backgroundColor: '#f59e0b' }}
                              onClick={() => onEdit(student)}
                            >
                              Edit
                            </button>
                            {isAfterSchoolStudent && (
                              <button
                                onClick={() => setEditingStudent(student)}
                                style={{ padding: '0.5rem 1rem', backgroundColor: '#00A3E0', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '500' }}
                              >
                                📅 Schedule
                              </button>
                            )}
                            <button
                              onClick={() => setInactiveModal(student)}
                              style={{ padding: '0.5rem 0.75rem', backgroundColor: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '4px', cursor: 'pointer', fontSize: '0.72rem', fontWeight: '600' }}
                            >
                              Discontinue
                            </button>
                          </div>
                        </td>
                        <td style={{ padding: '1rem', textAlign: 'center' }}>
                          {student.registrationComplete ? (
                            <span style={{ 
                              ...styles.badge, 
                              backgroundColor: '#d1fae5', 
                              color: '#065f46'
                            }}>
                              ✅ Complete
                            </span>
                          ) : (
                            <span style={{ 
                              ...styles.badge, 
                              backgroundColor: '#fef3c7', 
                              color: '#92400e'
                            }}>
                              ⚠️ Pending
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
            
            {activeStudents.length === 0 && (
              <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                <Users style={{ width: '48px', height: '48px', color: '#d1d5db', margin: '0 auto 1rem auto' }} />
                <p style={{ color: '#6b7280' }}>No students registered yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Inactive Students */}
      {showInactive && inactiveStudents.length > 0 && (
        <div style={{ ...styles.card, marginTop: '1.5rem', opacity: 0.85 }}>
          <h2 style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '1rem', color: '#6b7280' }}>
            Inactive Students ({inactiveStudents.length}) — Not enrolled, kept for records
          </h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                  {['Student', 'Grade', 'Program', 'Stopped', 'Reason', 'Notes', 'Action'].map(h => (
                    <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: '600', color: '#6b7280' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {inactiveStudents.map((s, i) => (
                  <tr key={s.id} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: i % 2 === 0 ? 'white' : '#fafafa' }}>
                    <td style={{ padding: '0.6rem 0.75rem', fontWeight: '500', color: '#6b7280' }}>{s.firstName} {s.lastName}</td>
                    <td style={{ padding: '0.6rem 0.75rem', color: '#9ca3af' }}>{s.grade || '-'}</td>
                    <td style={{ padding: '0.6rem 0.75rem', color: '#9ca3af' }}>{s.programSelection || '-'}</td>
                    <td style={{ padding: '0.6rem 0.75rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>{s.inactiveDate || '-'}</td>
                    <td style={{ padding: '0.6rem 0.75rem', color: '#9ca3af' }}>{s.inactiveReason || '-'}</td>
                    <td style={{ padding: '0.6rem 0.75rem', color: '#9ca3af', maxWidth: '160px' }}>{s.inactiveNotes || '-'}</td>
                    <td style={{ padding: '0.6rem 0.75rem' }}>
                      <button
                        onClick={() => handleReactivateStudent(s)}
                        style={{ padding: '0.3rem 0.65rem', backgroundColor: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7', borderRadius: '4px', cursor: 'pointer', fontSize: '0.72rem', fontWeight: '600' }}
                      >
                        Re-activate
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editingStudent && (
        <EditAfterSchoolStudentModal
          student={editingStudent}
          onClose={() => setEditingStudent(null)}
          onSave={handleSaveAfterSchoolStudent}
        />
      )}

      {/* Mark Inactive Modal */}
      {inactiveModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', width: '100%', maxWidth: '420px' }}>
            <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem', fontWeight: '700' }}>Discontinue Program</h3>
            <p style={{ margin: '0 0 1.25rem', fontSize: '0.85rem', color: '#6b7280' }}>
              {inactiveModal.firstName} {inactiveModal.lastName} will be removed from the roster but kept in All Students for records. You can re-activate them at any time.
            </p>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', marginBottom: '0.3rem' }}>Reason *</label>
              <select value={inactiveForm.reason} onChange={e => setInactiveForm(f => ({ ...f, reason: e.target.value }))} style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', boxSizing: 'border-box' }}>
                {STOP_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', marginBottom: '0.3rem' }}>Effective Date *</label>
              <input type="date" value={inactiveForm.effectiveDate} onChange={e => setInactiveForm(f => ({ ...f, effectiveDate: e.target.value }))} style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', boxSizing: 'border-box' }} />
              {inactiveForm.effectiveDate > new Date().toISOString().split('T')[0] && (
                <div style={{ fontSize: '0.75rem', color: '#d97706', marginTop: '0.3rem', backgroundColor: '#fffbeb', padding: '0.4rem 0.6rem', borderRadius: '4px' }}>
                  ⏳ Student stays active in the roster until {new Date(inactiveForm.effectiveDate + 'T12:00:00').toLocaleDateString()}
                </div>
              )}
            </div>
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', marginBottom: '0.3rem' }}>Notes (optional)</label>
              <textarea value={inactiveForm.notes} onChange={e => setInactiveForm(f => ({ ...f, notes: e.target.value }))} placeholder="Any additional context..." rows={2} style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setInactiveModal(null)} style={{ flex: 1, padding: '0.7rem', backgroundColor: '#6b7280', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
              <button onClick={handleMarkInactive} disabled={savingInactive} style={{ flex: 1, padding: '0.7rem', backgroundColor: savingInactive ? '#9ca3af' : '#dc2626', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
                {savingInactive ? 'Saving...' : 'Discontinue Program'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllStudents;